// Математика графиков без зависимостей: шкалы, «красивые» деления, монотонная кубическая
// интерполяция (как curveMonotoneX в d3 — плавно, но без ложных горбов между точками).

export interface Point {
  x: number;
  y: number;
}

export interface Scale {
  (v: number): number;
  domain: [number, number];
  range: [number, number];
  invert(px: number): number;
}

export function linearScale(domain: [number, number], range: [number, number]): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0);
  const fn = ((v: number) => r0 + (v - d0) * k) as Scale;
  fn.domain = domain;
  fn.range = range;
  fn.invert = (px: number) => (k === 0 ? d0 : d0 + (px - r0) / k);
  return fn;
}

/** Деления с «круглым» шагом (1 · 2 · 5 · 10 …); домен расширяется до ближайшего деления. */
export function niceTicks(
  min: number,
  max: number,
  count = 4,
): { ticks: number[]; min: number; max: number } {
  if (max <= min) max = min + 1;
  const rawStep = (max - min) / count;
  const pow = 10 ** Math.floor(Math.log10(rawStep));
  const norm = rawStep / pow;
  // Пороги как у d3.tickStep: 1.5 / 3 / 7 → шаги 1 · 2 · 5 · 10.
  const step = (norm >= 7 ? 10 : norm >= 3 ? 5 : norm >= 1.5 ? 2 : 1) * pow;
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return { ticks, min: niceMin, max: niceMax };
}

/** Путь плавной кривой через все точки (Fritsch–Carlson): наклоны ограничены, монотонность сохраняется. */
export function monotonePath(pts: Point[]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n === 1) return `M${pts[0].x},${pts[0].y}`;
  const dx: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x;
    m[i] = dx[i] === 0 ? 0 : (pts[i + 1].y - pts[i].y) / dx[i];
  }
  const t: number[] = new Array(n);
  t[0] = m[0];
  t[n - 1] = m[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      t[i] = 0;
    } else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      t[i] = (w1 + w2) / (w1 / m[i - 1] + w2 / m[i]);
    }
  }
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i];
    const c1x = pts[i].x + h / 3;
    const c1y = pts[i].y + (t[i] * h) / 3;
    const c2x = pts[i + 1].x - h / 3;
    const c2y = pts[i + 1].y - (t[i + 1] * h) / 3;
    d += ` C${c1x},${c1y} ${c2x},${c2y} ${pts[i + 1].x},${pts[i + 1].y}`;
  }
  return d;
}

/** Замкнутая область под кривой до базовой линии (для градиентной заливки). */
export function areaPath(pts: Point[], baselineY: number): string {
  if (pts.length === 0) return '';
  const line = monotonePath(pts);
  const last = pts[pts.length - 1];
  const first = pts[0];
  return `${line} L${last.x},${baselineY} L${first.x},${baselineY} Z`;
}

/** Индекс точки, ближайшей по X (бинарный поиск по отсортированному массиву). */
export function nearestIndex(xs: number[], x: number): number {
  let lo = 0;
  let hi = xs.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (xs[mid] < x) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(xs[lo - 1] - x) < Math.abs(xs[lo] - x)) return lo - 1;
  return lo;
}

export const formatTime = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
export const formatDay = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });

export function formatNumber(v: number, digits = 0): string {
  return new Intl.NumberFormat('ru-RU', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(v);
}
